const STORAGE_KEY = 'cdw-saved-project-v2';
const LEGACY_STORAGE_KEY = 'cdw-saved-project-v1';
const MAX_ITEMS = 80;

const emptyProject = () => ({
  items: [],
  roomName: '',
  notes: '',
  measurementLink: '',
  fileNames: [],
});

const normalizeItem = (item) => ({
  ...item,
  quantity: Math.max(1, Math.min(99, Number.parseInt(item.quantity, 10) || 1)),
  unitPrice: Number.parseInt(item.unitPrice, 10) || 0,
});

const readProject = () => {
  try {
    const stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || 'null');
    if (stored && Array.isArray(stored.items)) {
      return {
        ...emptyProject(),
        ...stored,
        items: stored.items.filter((item) => item && item.id && item.title).slice(0, MAX_ITEMS).map(normalizeItem),
        fileNames: Array.isArray(stored.fileNames) ? stored.fileNames.slice(0, 8) : [],
      };
    }

    const legacy = JSON.parse(window.localStorage.getItem(LEGACY_STORAGE_KEY) || '[]');
    if (Array.isArray(legacy) && legacy.length) {
      const project = { ...emptyProject(), items: legacy.slice(0, MAX_ITEMS).map(normalizeItem) };
      writeProject(project);
      return project;
    }
  } catch (_error) {
    // Continue with an empty schedule if storage is unavailable or malformed.
  }
  return emptyProject();
};

const writeProject = (project) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
      ...emptyProject(),
      ...project,
      items: project.items.slice(0, MAX_ITEMS),
    }));
  } catch (_error) {
    // The storefront remains usable when storage is unavailable.
  }
};

const roots = () => [...document.querySelectorAll('[data-cdw-project-root]')];

const itemFromButton = (button) => ({
  id: button.dataset.variantId || button.dataset.productId,
  productId: button.dataset.productId || '',
  variantId: button.dataset.variantId || '',
  title: button.dataset.productTitle || 'Cabinet product',
  sku: button.dataset.productSku || '',
  finish: button.dataset.productFinish || '',
  price: button.dataset.productPrice || '',
  unitPrice: Number.parseInt(button.dataset.productUnitPrice, 10) || 0,
  url: button.dataset.productUrl || '',
  image: button.dataset.productImage || '',
  quantity: 1,
});

const announce = (message) => {
  roots().forEach((root) => {
    const status = root.querySelector('[data-cdw-project-status]');
    if (status) status.textContent = message;
  });
};

const escapeHtml = (value) => String(value)
  .replaceAll('&', '&amp;')
  .replaceAll('<', '&lt;')
  .replaceAll('>', '&gt;')
  .replaceAll('"', '&quot;')
  .replaceAll("'", '&#039;');

const money = (cents) => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
}).format((Number(cents) || 0) / 100);

const projectSubtotal = (items) => items.reduce((sum, item) => sum + (item.unitPrice * item.quantity), 0);

const scheduleLines = (items) => items.map((item) => {
  const details = [item.sku, item.finish].filter(Boolean).join(' · ');
  return `${item.quantity} × ${item.title}${details ? ` — ${details}` : ''}`;
});

const quoteUrl = (project) => {
  const params = new URLSearchParams();
  params.set('reason', 'Project quote and availability review');
  params.set('skus', project.items.map((item) => `${item.quantity}x ${item.sku}`).filter(Boolean).join(', '));
  params.set('products', scheduleLines(project.items).join('\n'));
  params.set('source', 'Cabinet schedule');
  if (project.roomName) params.set('room', project.roomName);
  if (project.notes) params.set('notes', project.notes);
  if (project.measurementLink) params.set('measurement_link', project.measurementLink);
  if (project.fileNames.length) params.set('measurement_files', project.fileNames.join(', '));
  params.set('subtotal', money(projectSubtotal(project.items)));
  return `/pages/contact?${params.toString()}#design-request`;
};

const itemMarkup = (item) => {
  const image = item.image
    ? `<img src="${escapeHtml(item.image)}" alt="" width="72" height="72" loading="lazy">`
    : '<span class="cdw-project__image-placeholder" aria-hidden="true"></span>';
  const finish = item.finish ? `<span>${escapeHtml(item.finish)}</span>` : '';
  const sku = item.sku ? `<span>SKU ${escapeHtml(item.sku)}</span>` : '';
  const price = item.unitPrice ? `<strong>${money(item.unitPrice * item.quantity)}</strong>` : `<strong>${escapeHtml(item.price || 'Price review')}</strong>`;
  return `<li class="cdw-project__item" data-cdw-project-row="${escapeHtml(item.id)}">
    <a href="${escapeHtml(item.url || '#')}">${image}</a>
    <div class="cdw-project__item-copy">
      <a href="${escapeHtml(item.url || '#')}">${escapeHtml(item.title)}</a>
      <p>${sku}${finish}</p>
      <label class="cdw-project__quantity">
        <span>Qty</span>
        <input type="number" min="1" max="99" inputmode="numeric" value="${item.quantity}" data-cdw-project-quantity="${escapeHtml(item.id)}" aria-label="Quantity for ${escapeHtml(item.title)}">
      </label>
      ${price}
    </div>
    <button type="button" data-cdw-project-remove="${escapeHtml(item.id)}" aria-label="Remove ${escapeHtml(item.title)} from cabinet schedule">Remove</button>
  </li>`;
};

const setInputValue = (root, selector, value) => {
  const input = root.querySelector(selector);
  if (input && document.activeElement !== input) input.value = value || '';
};

const render = () => {
  const project = readProject();
  const savedIds = new Set(project.items.map((item) => String(item.id)));
  const totalUnits = project.items.reduce((sum, item) => sum + item.quantity, 0);

  document.querySelectorAll('[data-cdw-mobile-project-open]').forEach((button) => {
    button.toggleAttribute('data-has-items', project.items.length > 0);
  });
  document.querySelectorAll('[data-cdw-mobile-project-count]').forEach((count) => {
    count.textContent = String(totalUnits);
  });

  document.querySelectorAll('[data-cdw-project-save]').forEach((button) => {
    const id = button.dataset.variantId || button.dataset.productId;
    const saved = savedIds.has(String(id));
    button.setAttribute('aria-pressed', String(saved));
    const label = button.querySelector('[data-cdw-save-label]');
    if (label) label.textContent = saved ? 'Saved to schedule' : button.dataset.defaultLabel || 'Save to project';
  });

  roots().forEach((root) => {
    const launcher = root.querySelector('[data-cdw-project-open]');
    const count = root.querySelector('[data-cdw-project-count]');
    const list = root.querySelector('[data-cdw-project-items]');
    const empty = root.querySelector('[data-cdw-project-empty]');
    const clear = root.querySelector('[data-cdw-project-clear]');
    const quote = root.querySelector('[data-cdw-project-quote]');
    const subtotal = root.querySelector('[data-cdw-project-subtotal]');
    const units = root.querySelector('[data-cdw-project-units]');
    const files = root.querySelector('[data-cdw-project-file-list]');

    if (launcher) launcher.hidden = project.items.length === 0;
    if (count) count.textContent = String(totalUnits);
    if (list) list.innerHTML = project.items.map(itemMarkup).join('');
    if (empty) empty.hidden = project.items.length !== 0;
    if (clear) clear.hidden = project.items.length === 0;
    if (subtotal) subtotal.textContent = money(projectSubtotal(project.items));
    if (units) units.textContent = `${totalUnits} cabinet unit${totalUnits === 1 ? '' : 's'} · ${project.items.length} unique SKU${project.items.length === 1 ? '' : 's'}`;
    if (files) files.textContent = project.fileNames.length ? `Selected locally: ${project.fileNames.join(', ')}` : 'No local files selected.';
    setInputValue(root, '[data-cdw-project-room]', project.roomName);
    setInputValue(root, '[data-cdw-project-notes]', project.notes);
    setInputValue(root, '[data-cdw-project-measurement-link]', project.measurementLink);
    if (quote) {
      quote.href = project.items.length ? quoteUrl(project) : '/pages/contact#design-request';
      quote.textContent = project.items.length ? 'Request one project quote' : 'Request project review';
      quote.setAttribute('aria-disabled', String(project.items.length === 0));
    }
  });
};

const updateProjectField = (field, value) => {
  const project = readProject();
  project[field] = value;
  writeProject(project);
};

const syncMobileDockRoute = () => {
  const dock = document.querySelector('[data-cdw-mobile-dock]');
  if (!dock) return;

  const projectDialog = document.querySelector('[data-cdw-project-dialog]');
  const activeTab = projectDialog?.open
    ? 'project'
    : window.location.hash === '#finish-collections'
      ? 'finishes'
      : dock.dataset.cdwActiveTab;

  dock.querySelectorAll('[data-cdw-app-tab]').forEach((tab) => {
    if (tab.dataset.cdwAppTab === activeTab) tab.setAttribute('aria-current', 'page');
    else tab.removeAttribute('aria-current');
    if (tab.matches('[data-cdw-mobile-project-open]')) {
      tab.setAttribute('aria-expanded', String(Boolean(projectDialog?.open)));
    }
  });
};

const exportSchedule = () => {
  const project = readProject();
  if (!project.items.length) return;
  const csvRows = [
    ['Room', 'Quantity', 'SKU', 'Finish', 'Product', 'Unit price', 'Line subtotal', 'URL'],
    ...project.items.map((item) => [
      project.roomName,
      item.quantity,
      item.sku,
      item.finish,
      item.title,
      money(item.unitPrice),
      money(item.unitPrice * item.quantity),
      new URL(item.url || '/', window.location.origin).toString(),
    ]),
  ];
  const csv = csvRows.map((row) => row.map((value) => `"${String(value || '').replaceAll('"', '""')}"`).join(',')).join('\n');
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = `cdw-cabinet-schedule-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

const prefillContact = () => {
  const form = document.querySelector('[data-cdw-contact-form]');
  if (!form) return;

  const params = new URLSearchParams(window.location.search);
  const reason = params.get('reason');
  const skus = params.get('skus') || params.get('sku');
  const products = params.get('products') || params.get('product');
  const source = params.get('source');
  const room = params.get('room');
  const notes = params.get('notes');
  const measurementLink = params.get('measurement_link');
  const measurementFiles = params.get('measurement_files');
  const subtotal = params.get('subtotal');
  const reasonField = form.querySelector('[data-cdw-contact-reason]');
  const skuField = form.querySelector('[data-cdw-contact-skus]');
  const messageField = form.querySelector('[data-cdw-contact-message]');
  const sourceField = form.querySelector('[data-cdw-contact-source]');

  if (reason && reasonField) {
    const matchingOption = [...reasonField.options].find((option) => option.textContent.trim() === reason);
    if (matchingOption) reasonField.value = matchingOption.value;
  }
  if (skus && skuField && !skuField.value) skuField.value = skus;
  if (products && messageField && !messageField.value) {
    const projectDetails = [
      `Please review availability and project pricing for:\n${products}`,
      room ? `Room / project: ${room}` : null,
      subtotal ? `Working product subtotal: ${subtotal}` : null,
      measurementLink ? `Measurement share link: ${measurementLink}` : null,
      measurementFiles ? `Measurement files selected locally: ${measurementFiles}` : null,
      notes ? `Room notes: ${notes}` : null,
      'Additional project details:',
    ].filter(Boolean);
    messageField.value = projectDetails.join('\n\n');
  }
  if (source && sourceField) sourceField.value = source;
};

document.addEventListener('input', (event) => {
  const quantity = event.target.closest('[data-cdw-project-quantity]');
  if (quantity instanceof HTMLInputElement) {
    const project = readProject();
    const item = project.items.find((row) => String(row.id) === String(quantity.dataset.cdwProjectQuantity));
    if (item) item.quantity = Math.max(1, Math.min(99, Number.parseInt(quantity.value, 10) || 1));
    writeProject(project);
    render();
    return;
  }

  const room = event.target.closest('[data-cdw-project-room]');
  if (room instanceof HTMLInputElement) updateProjectField('roomName', room.value);
  const notes = event.target.closest('[data-cdw-project-notes]');
  if (notes instanceof HTMLTextAreaElement) updateProjectField('notes', notes.value);
  const link = event.target.closest('[data-cdw-project-measurement-link]');
  if (link instanceof HTMLInputElement) updateProjectField('measurementLink', link.value);
});

document.addEventListener('change', (event) => {
  const fileInput = event.target.closest('[data-cdw-project-files]');
  if (!(fileInput instanceof HTMLInputElement)) return;
  const project = readProject();
  project.fileNames = [...(fileInput.files || [])].slice(0, 8).map((file) => file.name);
  writeProject(project);
  render();
  announce('Measurement file names added. Use a share link or attach the files when the project team replies.');
});

document.addEventListener('click', (event) => {
  const saveButton = event.target.closest('[data-cdw-project-save]');
  if (saveButton) {
    event.preventDefault();
    event.stopPropagation();
    const item = itemFromButton(saveButton);
    const project = readProject();
    const index = project.items.findIndex((saved) => String(saved.id) === String(item.id));
    if (index >= 0) {
      project.items.splice(index, 1);
      announce(`${item.title} removed from your cabinet schedule.`);
    } else {
      project.items.unshift(item);
      announce(`${item.title} saved to your cabinet schedule.`);
    }
    writeProject(project);
    render();
    return;
  }

  const openButton = event.target.closest('[data-cdw-project-open], [data-cdw-mobile-project-open]');
  if (openButton) {
    const dialog = openButton.closest('[data-cdw-project-root]')?.querySelector('[data-cdw-project-dialog]')
      || document.querySelector('[data-cdw-project-dialog]');
    if (dialog && !dialog.open) {
      try { dialog.showModal(); } catch (_error) { dialog.setAttribute('open', ''); }
      if (!dialog.open) dialog.setAttribute('open', '');
    }
    syncMobileDockRoute();
    return;
  }

  const closeButton = event.target.closest('[data-cdw-project-close]');
  if (closeButton) {
    const dialog = closeButton.closest('dialog');
    if (dialog) {
      try { dialog.close(); } catch (_error) { dialog.removeAttribute('open'); }
      if (dialog.open) dialog.removeAttribute('open');
    }
    syncMobileDockRoute();
    return;
  }

  const removeButton = event.target.closest('[data-cdw-project-remove]');
  if (removeButton) {
    const project = readProject();
    project.items = project.items.filter((item) => String(item.id) !== String(removeButton.dataset.cdwProjectRemove));
    writeProject(project);
    render();
    announce('Item removed from your cabinet schedule.');
    return;
  }

  if (event.target.closest('[data-cdw-project-export]')) {
    exportSchedule();
    return;
  }

  if (event.target.closest('[data-cdw-project-clear]')) {
    const project = readProject();
    project.items = [];
    writeProject(project);
    render();
    announce('Cabinet schedule cleared.');
  }
});

document.addEventListener('shopify:section:load', render);
window.addEventListener('hashchange', syncMobileDockRoute);
document.addEventListener('cdw:catalog:updated', render);
document.addEventListener('shopify:collection:update', (event) => {
  event.detail?.promise?.then(() => window.requestAnimationFrame(render));
});
document.addEventListener('shopify:product:select', (event) => {
  event.detail?.promise?.then(() => window.requestAnimationFrame(render));
});
document.addEventListener('DOMContentLoaded', () => {
  render();
  prefillContact();
  syncMobileDockRoute();
  document.querySelectorAll('[data-cdw-project-dialog]').forEach((dialog) => {
    dialog.addEventListener('close', syncMobileDockRoute);
  });
});
